import { FunctionComponent, useMemo } from "react";

import { faGear } from "@fortawesome/pro-regular-svg-icons";

import { Dropdown } from "@/components/Dropdown/Dropdown";
import { Icon } from "@/components/Icon/Icon";
import { User } from "@/pages/Public/pages/Login/schema/login";

import { FrameworkOverrides, LANGUAGE_NAME_MAP } from "./types";
import { FrameworksRow } from "./FrameworksRow";
import { LanguageFlag } from "./LanguageFlag";

interface BatchSettingsPanelProps {
	batchSize: number;
	setBatchSize: (n: number) => void;
	language: string;
	setLanguage: (l: string) => void;
	trigger: string;
	setTrigger: (t: string) => void;
	senderMode: "randomized" | "specific";
	selectedSender: User | null;
	setSenderMode: (mode: "randomized" | "specific") => void;
	setSelectedSender: (user: User | null) => void;
	availableUsers: User[];
	isGenerating: boolean;
	frameworkOverrides: FrameworkOverrides;
	onOpenLanguageModal: () => void;
	onOpenFrameworksModal: () => void;
}

const BATCH_SIZES = [5, 10, 15, 20];

export const BatchSettingsPanel: FunctionComponent<BatchSettingsPanelProps> = ({
	batchSize,
	setBatchSize,
	language,
	setLanguage,
	trigger,
	setTrigger,
	senderMode,
	selectedSender,
	setSenderMode,
	setSelectedSender,
	availableUsers,
	isGenerating,
	frameworkOverrides,
	onOpenLanguageModal,
	onOpenFrameworksModal,
}) => {
	const languageOptions = useMemo(() => {
		return [
			{ id: "auto", title: "Auto-detect" },
			...Object.entries(LANGUAGE_NAME_MAP).map(([code, name]) => ({
				id: code,
				title: name,
				icon: <LanguageFlag className="w-4 h-auto" language={code} title={code.toUpperCase()} />,
			})),
		];
	}, []);

	const triggerOptions = useMemo(() => [{ id: "auto", title: "Auto-detect" }], []);

	const senderOptions = useMemo(() => {
		const options: { id: string | number; title: string }[] = [
			{ id: "randomized", title: "Randomized" },
		];

		availableUsers.forEach((u) => {
			options.push({ id: u.id, title: `${u.firstName} ${u.lastName}` });
		});

		return options;
	}, [availableUsers]);

	const currentLanguageItem = useMemo(() => {
		if (language === "auto") {
			return { id: "auto", title: "Auto-detect" };
		}

		return {
			id: language,
			title: LANGUAGE_NAME_MAP[language] || language.toUpperCase(),
			icon: (
				<LanguageFlag className="w-4 h-auto" language={language} title={language.toUpperCase()} />
			),
		};
	}, [language]);

	return (
		<div className="flex flex-col gap-3 pt-3 border-t border-border-light">
			{/* Lead count */}
			<div className="flex items-center gap-2">
				<span className="text-sm text-text-meta shrink-0 w-20">Lead count</span>
				<Dropdown
					floating
					data={BATCH_SIZES.map((n) => ({ id: n, title: `${n} leads` }))}
					disabled={isGenerating}
					handleSelect={(item) => setBatchSize(Number(item.id))}
					value={{ id: batchSize, title: `${batchSize} leads` }}
				/>
			</div>

			{/* Language */}
			<div className="flex items-center gap-2">
				<span className="text-sm text-text-meta shrink-0 w-20">Language</span>
				<Dropdown
					floating
					data={languageOptions}
					disabled={isGenerating}
					handleSelect={(item) => setLanguage(item.id as string)}
					value={currentLanguageItem}
				/>
				<button
					className="p-1 rounded-md hover:bg-gray-100 text-text-meta transition-colors disabled:opacity-50"
					disabled={isGenerating}
					type="button"
					onClick={onOpenLanguageModal}
				>
					<Icon className="w-4 h-4" icon={faGear} />
				</button>
			</div>

			{/* Sender */}
			<div className="flex items-center gap-2">
				<span className="text-sm text-text-meta shrink-0 w-20">Sender</span>
				<Dropdown
					floating
					data={senderOptions}
					disabled={isGenerating}
					handleSelect={(item) => {
						if (item.id === "randomized") {
							setSenderMode("randomized");
							setSelectedSender(null);
						} else {
							setSenderMode("specific");
							const user = availableUsers.find((u) => u.id === item.id);

							setSelectedSender(user || null);
						}
					}}
					value={
						senderMode === "randomized"
							? { id: "randomized", title: "Randomized" }
							: selectedSender
								? {
										id: selectedSender.id,
										title: `${selectedSender.firstName} ${selectedSender.lastName}`,
									}
								: undefined
					}
				/>
			</div>

			{/* Frameworks */}
			<FrameworksRow
				frameworkOverrides={frameworkOverrides}
				isGenerating={isGenerating}
				onOpenFrameworksModal={onOpenFrameworksModal}
			/>
		</div>
	);
};
